// ============================================================================
// useAgentChat — thin facade over the shared AgentChatProvider store
//
// This hook exists for backward compatibility so that existing callers
// (Home.js, AutoMateBubble.jsx) continue to work without code changes.
// It delegates entirely to `useAgentChatStore()` from the provider and
// returns the same public API shape the original hook exposed.
//
// Migration note: callers should eventually import `useAgentChatStore`
// directly from the provider module. This facade will be removed once all
// consumers are updated.
// ============================================================================

import { useAgentChatStore } from '../context/AgentChatProvider';

/**
 * Hook that provides the agent chat API by delegating to the shared
 * AgentChatProvider store.
 *
 * Returns { messages, isStreaming, waitingReason, handleChatSubmit, handleCancel, handleNewChat }.
 *
 * messages: Array of {
 *   role: 'user' | 'assistant',
 *   content: string,
 *   durationMs?: number,
 *   timeToFirstTokenMs?: number,
 *   sources?: Array<{ documentId?: string, title?: string, excerpt?: string, s3Uri?: string, sourceUrl?: string }>,
 *   correlationId?: string,
 * }
 * isStreaming: true while the assistant is mid-response
 * waitingReason: null | 'first-token' | 'inter-chunk' — drives a "still
 *   waiting" banner in the ChatPanel.
 * handleChatSubmit: (message: string) => void — send a message to the agent
 * handleCancel: () => void — tear down the current request immediately
 * handleNewChat: () => void — clear conversation and start fresh (Req 12.6)
 */
const useAgentChat = () => {
  const {
    messages,
    isStreaming,
    waitingReason,
    handleChatSubmit,
    handleCancel,
    handleNewChat,
  } = useAgentChatStore();

  return { messages, isStreaming, waitingReason, handleChatSubmit, handleCancel, handleNewChat };
};

export default useAgentChat;
